import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login.jsx';
import { ThemeProvider } from "./context/ThemeContext";
import { API_BASE } from './config';

// Mode ke darwaze par rok. Backend to pehle se rokta hai (T11.5), par uske bina
// screen khaali render ho jaati thi aur user samajhta ki "data hi nahi hai" —
// end-to-end test me HRMS aur Ward dono par yahi mila. Wajah → components/ModuleGuard.jsx
//
// Ye baaki import ke saath UPAR hona chahiye: `lazy()` wali const lines ke beech
// rakhne par production build `import/first` par fail ho jaati hai (dev server
// ise sirf warning maanta hai, isliye der se pakda gaya).
import ModuleGuard from './components/ModuleGuard';

const Layout = lazy(() => import('./components/MainLayout/Layout.jsx'));
const UserCreation = lazy(() => import('./components/UserCreation.jsx'));
const UserRolesManager = lazy(() => import('./components/UserRolesManager.jsx'));
const RoleModelManager = lazy(() => import('./pages/RoleModelManager.jsx'));   // T12.1 — Tier 2 role model
const UsersTable = lazy(() => import('./components/UsersTable.jsx'));
const UserRolesTable = lazy(() => import('./components/UserRolesTable.jsx'));
const IPDServiceDetailsTable = lazy(() => import('./components/IPDServiceDetailsTable.jsx'));
const AIChatPage = lazy(() => import('./components/AIChatPage.jsx'));
const HospitalProfile = lazy(() => import('./components/HospitalProfile.jsx'));
const ReceiptSettings = lazy(() => import('./components/ReceiptSettings.js'));
const PackageMaster = lazy(() => import('./components/PackageMaster.js'));
const USGReportHeaderConfig = lazy(() => import('./components/USGReportHeaderConfig.jsx'));
const PharmacyLayout = lazy(() => import('./components/PharmacyMode/PharmacyLayout.jsx'));
const AdminReportLayout = lazy(() => import('./components/AdminReportLayout.jsx'));
const ClinicalLayout = lazy(() => import('./components/ClinicalMode/ClinicalLayout.jsx'));
const WardLayout = lazy(() => import('./components/WardMode/WardLayout.jsx'));
const HrmsLayout = lazy(() => import('./components/HrmsMode/HrmsLayout.jsx'));
const InventoryLayout = lazy(() => import('./components/InventoryMode/InventoryLayout.jsx'));
const BillingLayout = lazy(() => import('./components/BillingMode/BillingLayout'));
const MasterLayout = lazy(() => import('./components/MasterMode/MasterLayout'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const PatientRegister = lazy(() => import('./pages/PatientRegister.jsx'));
const OPDList = lazy(() => import('./pages/OPDList'));

const IpdList = lazy(() => import('./pages/IpdList.jsx'));
const IpdAdvanceForm = lazy(() => import('./pages/IpdAdvanceForm.jsx'));
const IPDServiceDetailForm = lazy(() => import('./pages/IPDServiceDetailForm.jsx'));
const RoomForm = lazy(() => import('./pages/RoomForm.jsx'));
const EditRoomForm = lazy(() => import('./pages/EditRoomForm.jsx'));
const IPDRoomBookingForm = lazy(() => import('./pages/IPDRoomBookingForm.jsx'));
const DepartmentForm = lazy(() => import('./pages/DepartmentForm.jsx'));
const EditDepartmentList = lazy(() => import('./pages/EditDepartmentList.jsx'));
const EmployeeForm = lazy(() => import('./pages/EmployeeForm.jsx'));
const EditEmployeeList = lazy(() => import('./pages/EditEmployeeList.jsx'));
const IPDRoomUpdate = lazy(() => import('./pages/IPDRoomUpdate.jsx'));
const IPDAdvanceTable = lazy(() => import('./pages/IPDAdvanceTable.jsx'));
const DischargeBill = lazy(() => import('./pages/DischargeBill.jsx'));
const DischargeList = lazy(() => import('./pages/DischargeList.jsx'));
const BillingForm = lazy(() => import('./pages/BillingForm.jsx'));
const BillList = lazy(() => import('./pages/BillList.jsx'));
const Billpopup = lazy(() => import('./pages/Billpopup.jsx'));
const BillingPayment = lazy(() => import('./pages/BillingPayment.jsx'));
const ReportBillSummary = lazy(() => import('./pages/ReportBillSummary.jsx'));
const ReportBillPayment = lazy(() => import('./pages/ReportBillPayment.jsx'));
const ReportDue = lazy(() => import('./pages/ReportDue.jsx'));
const Report = lazy(() => import('./pages/Report.jsx'));
const ExPDTForm = lazy(() => import('./pages/ExPDTForm.jsx'));
const AccountMasterForm = lazy(() => import('./pages/AccountMasterForm.jsx'));
const BankDTForm = lazy(() => import('./pages/BankDTForm.jsx'));
const ExPDTSearchForm = lazy(() => import('./pages/ExPDTSearchForm.jsx'));
const DaybookReport = lazy(() => import('./pages/DaybookReport.jsx'));
const DiscountAuthorityForm = lazy(() => import('./pages/DiscountAuthorityForm.jsx'));
const SendByMasterForm = lazy(() => import('./pages/SendByMasterForm.jsx'));
const PatientDocuments = lazy(() => import('./pages/PatientDocuments.jsx'));
const AdvanceRegistrationList = lazy(() => import('./pages/AdvanceRegistrationList.jsx'));
const EstimateBillList = lazy(() => import('./pages/EstimateBillList.jsx'));
const PharmaCompanyForm = lazy(() => import('./pages/PharmaCompanyForm.jsx'));
const MedCategoryDTForm = lazy(() => import('./pages/MedCategoryForm.jsx'));
const MedBrandForm = lazy(() => import('./pages/MedBrandForm.jsx'));
const MedicineForm = lazy(() => import('./pages/MedicineForm.jsx'));
const MedicineSearchList = lazy(() => import('./pages/MedicineSearchList.jsx'));
const VendorDTForm = lazy(() => import('./pages/VendorDTForm.jsx'));
const MedPurchase = lazy(() => import('./pages/MedPurchase.jsx'));
const MedicinePurchaseSearch = lazy(() => import('./pages/MedicinePurchaseSearch.jsx'));
const MedicineSaleForm = lazy(() => import('./pages/MedicineSaleForm.jsx'));
const GstIpdBilling = lazy(() => import('./pages/GstIpdBilling.jsx'));
const GstServiceMaster = lazy(() => import('./pages/GstServiceMaster.jsx'));
const GstReport = lazy(() => import('./pages/GstReport.jsx'));
const GstBillList = lazy(() => import('./pages/GstBillList.jsx'));
const MedicineSaleSearch = lazy(() => import('./pages/MedicineSaleSearch.jsx'));
const MedicineSaleEditModal = lazy(() => import('./pages/MedicineSaleEditModal.jsx'));
const StockOutForm = lazy(() => import('./pages/StockOutForm.jsx'));
const StockOutSearch = lazy(() => import('./pages/StockOutSearch.jsx'));
const DebitVoucher = lazy(() => import('./pages/DebitVoucher.jsx'));
const DebitVoucherSearch = lazy(() => import('./pages/DebitVoucherSearch.jsx'));
const DebitVoucherEditModal = lazy(() => import('./pages/DebitVoucherEditModal.jsx'));
const PharmacyDashboard = lazy(() => import('./pages/PharmacyDashboard.jsx'));
const InventoryReports = lazy(() => import('./pages/InventoryReports.jsx'));
const SalesReports = lazy(() => import('./pages/SalesReports.jsx'));
const MedProfitReport = lazy(() => import('./pages/MedProfitReport.jsx'));
const MedDeadStockReport = lazy(() => import('./pages/MedDeadStockReport.jsx'));
const MedCustomerDueReport = lazy(() => import('./pages/MedCustomerDueReport.jsx'));
const MedDiscountReport = lazy(() => import('./pages/MedDiscountReport.jsx'));
const MedVendorStatement = lazy(() => import('./pages/MedVendorStatement.jsx'));
const MedBatchStockReport = lazy(() => import('./pages/MedBatchStockReport.jsx'));
const MedH1Tagging = lazy(() => import('./pages/MedH1Tagging.jsx'));
const MedH1Register = lazy(() => import('./pages/MedH1Register.jsx'));
const MedConsumptionReport = lazy(() => import('./pages/MedConsumptionReport.jsx'));
const PurchaseReports = lazy(() => import('./pages/PurchaseReports.jsx'));
const ExpiryReports = lazy(() => import('./pages/ExpiryReports.jsx'));
const FinancialReports = lazy(() => import('./pages/FinancialReports.jsx'));
const DashboardReport = lazy(() => import('./pages/DashboardReport.jsx'));
const ChargeMaster = lazy(() => import('./pages/ChargeMaster.jsx'));
const PrescriptionCreate = lazy(() => import('./pages/PrescriptionCreate.jsx'));
const PrescriptionHistory = lazy(() => import('./pages/PrescriptionHistory.jsx'));
const IpdReport = lazy(() => import('./pages/IpdReport.jsx'));
const UsgMasterPage = lazy(() => import('./pages/UsgMaster.jsx'));
const UsgReportPage = lazy(() => import('./pages/UsgReport.jsx'));
const UsgReportHistory = lazy(() => import('./pages/UsgReportHistory.jsx'));
const VendorPaymentForm = lazy(() => import('./pages/VendorPaymentForm.jsx'));
const FinancialLedger = lazy(() => import('./pages/FinancialLedger.jsx'));


const WardDashboard = lazy(() => import('./pages/Ward/WardDashboard.jsx'));
const WardPatientChart = lazy(() => import('./pages/Ward/WardPatientChart.jsx'));
const HrmsDashboard = lazy(() => import('./pages/Hrms/HrmsDashboard.jsx'));
const HrmsCxoDashboard = lazy(() => import('./pages/Hrms/HrmsCxoDashboard.jsx'));
const InventoryDashboard = lazy(() => import('./pages/Inventory/InventoryDashboard.jsx'));
const InventoryPlaceholder = lazy(() => import('./pages/Inventory/InventoryPlaceholder.jsx'));
const InvCategory = lazy(() => import('./pages/Inventory/InvCategory.jsx'));
const InvUnit = lazy(() => import('./pages/Inventory/InvUnit.jsx'));
const InvStore = lazy(() => import('./pages/Inventory/InvStore.jsx'));
const InvVendor = lazy(() => import('./pages/Inventory/InvVendor.jsx'));
const InvProduct = lazy(() => import('./pages/Inventory/InvProduct.jsx'));
const InvOpeningStock = lazy(() => import('./pages/Inventory/InvOpeningStock.jsx'));
const InvStock = lazy(() => import('./pages/Inventory/InvStock.jsx'));
const InvLowStock = lazy(() => import('./pages/Inventory/InvLowStock.jsx'));
const InvLedger = lazy(() => import('./pages/Inventory/InvLedger.jsx'));
const InvPurchaseOrder = lazy(() => import('./pages/Inventory/InvPurchaseOrder.jsx'));
const InvPurchase = lazy(() => import('./pages/Inventory/InvPurchase.jsx'));
const InvPurchaseSearch = lazy(() => import('./pages/Inventory/InvPurchaseSearch.jsx'));
const InvIssue = lazy(() => import('./pages/Inventory/InvIssue.jsx'));
const InvIssueSearch = lazy(() => import('./pages/Inventory/InvIssueSearch.jsx'));
const InvConsumption = lazy(() => import('./pages/Inventory/InvConsumption.jsx'));
const InvConsumptionSearch = lazy(() => import('./pages/Inventory/InvConsumptionSearch.jsx'));
const InvStockOut = lazy(() => import('./pages/Inventory/InvStockOut.jsx'));
const InvDeptStockOut = lazy(() => import('./pages/Inventory/InvDeptStockOut.jsx'));
const InvVerify = lazy(() => import('./pages/Inventory/InvVerify.jsx'));
const InvVendorPayment = lazy(() => import('./pages/Inventory/InvVendorPayment.jsx'));
const InvDebitVoucher = lazy(() => import('./pages/Inventory/InvDebitVoucher.jsx'));
const InvVendorOutstanding = lazy(() => import('./pages/Inventory/InvVendorOutstanding.jsx'));
const InvVendorLedger = lazy(() => import('./pages/Inventory/InvVendorLedger.jsx'));
const InvAssets = lazy(() => import('./pages/Inventory/InvAssets.jsx'));
const InvAssetComplaints = lazy(() => import('./pages/Inventory/InvAssetComplaints.jsx'));
const InvReports = lazy(() => import('./pages/Inventory/InvReports.jsx'));
const HrmsEmployees = lazy(() => import('./pages/Hrms/HrmsEmployees.jsx'));
const HrmsEmployeeProfile = lazy(() => import('./pages/Hrms/HrmsEmployeeProfile.jsx'));
const HrmsShifts = lazy(() => import('./pages/Hrms/HrmsShifts.jsx'));
const HrmsHolidays = lazy(() => import('./pages/Hrms/HrmsHolidays.jsx'));
const HrmsAttendance = lazy(() => import('./pages/Hrms/HrmsAttendance.jsx'));
const HrmsAttendanceMonthly = lazy(() => import('./pages/Hrms/HrmsAttendanceMonthly.jsx'));
const HrmsBiometricImport = lazy(() => import('./pages/Hrms/HrmsBiometricImport.jsx'));
const HrmsLeave = lazy(() => import('./pages/Hrms/HrmsLeave.jsx'));
const HrmsLeaveTypes = lazy(() => import('./pages/Hrms/HrmsLeaveTypes.jsx'));
const HrmsLeaveBalance = lazy(() => import('./pages/Hrms/HrmsLeaveBalance.jsx'));
const HrmsSalaryComponents = lazy(() => import('./pages/Hrms/HrmsSalaryComponents.jsx'));
const HrmsSalaryTemplates = lazy(() => import('./pages/Hrms/HrmsSalaryTemplates.jsx'));
const HrmsSalaryAllot = lazy(() => import('./pages/Hrms/HrmsSalaryAllot.jsx'));
const HrmsAdvances = lazy(() => import('./pages/Hrms/HrmsAdvances.jsx'));
const HrmsVerify = lazy(() => import('./pages/Hrms/HrmsVerify.jsx'));
const HrmsVouchers = lazy(() => import('./pages/Hrms/HrmsVouchers.jsx'));
const HrmsPayrollRun = lazy(() => import('./pages/Hrms/HrmsPayrollRun.jsx'));
const HrmsPayrollSlips = lazy(() => import('./pages/Hrms/HrmsPayrollSlips.jsx'));
const HrmsCandidates = lazy(() => import('./pages/Hrms/HrmsCandidates.jsx'));
const HrmsAppointments = lazy(() => import('./pages/Hrms/HrmsAppointments.jsx'));
const HrmsTrainings = lazy(() => import('./pages/Hrms/HrmsTrainings.jsx'));
const HrmsReports = lazy(() => import('./pages/Hrms/HrmsReports.jsx'));
const HrmsAudit = lazy(() => import('./pages/Hrms/HrmsAudit.jsx'));
const HrmsDocExpiry = lazy(() => import('./pages/Hrms/HrmsDocExpiry.jsx'));
const HrmsExit = lazy(() => import('./pages/Hrms/HrmsExit.jsx'));
const HrmsExitDetail = lazy(() => import('./pages/Hrms/HrmsExitDetail.jsx'));
const HrmsOvertime = lazy(() => import('./pages/Hrms/HrmsOvertime.jsx'));
const HrmsRoster = lazy(() => import('./pages/Hrms/HrmsRoster.jsx'));
const HrmsAttRegularize = lazy(() => import('./pages/Hrms/HrmsAttRegularize.jsx'));
const HrmsLeaveEncash = lazy(() => import('./pages/Hrms/HrmsLeaveEncash.jsx'));
const HrmsLoan = lazy(() => import('./pages/Hrms/HrmsLoan.jsx'));
const HrmsBonus = lazy(() => import('./pages/Hrms/HrmsBonus.jsx'));
const HrmsTaxPlan = lazy(() => import('./pages/Hrms/HrmsTaxPlan.jsx'));
const HrmsTaxDeclaration = lazy(() => import('./pages/Hrms/HrmsTaxDeclaration.jsx'));
const HrmsTaxConfig = lazy(() => import('./pages/Hrms/HrmsTaxConfig.jsx'));
const HrmsChallan = lazy(() => import('./pages/Hrms/HrmsChallan.jsx'));
const HrmsAppraisal = lazy(() => import('./pages/Hrms/HrmsAppraisal.jsx'));
const HrmsAssets = lazy(() => import('./pages/Hrms/HrmsAssets.jsx'));
const HrmsManual = lazy(() => import('./pages/Hrms/HrmsManual.jsx'));
const HrmsForm16 = lazy(() => import('./pages/Hrms/HrmsForm16.jsx'));

// ESS — employee ka apna portal. Jaanbujhkar /hrms se bahar, apne route tree me.
const EssLayout = lazy(() => import('./ess/EssLayout.jsx'));
const EssLogin = lazy(() => import('./ess/EssLogin.jsx'));
const EssChangePassword = lazy(() => import('./ess/EssChangePassword.jsx'));
const EssHome = lazy(() => import('./ess/EssHome.jsx'));
const EssSalary = lazy(() => import('./ess/EssSalary.jsx'));
const EssAttendance = lazy(() => import('./ess/EssAttendance.jsx'));
const EssLeave = lazy(() => import('./ess/EssLeave.jsx'));
const EssProfile = lazy(() => import('./ess/EssProfile.jsx'));

// ESS ke routes DONO branch me chahiye — logged-out aur logged-in.
//
// Pehle ye galti se sirf logged-out branch me the. Nateeja: browser me staff ka session
// khula ho to /ess/login seedha '/' par phenk diya jaata tha (logged-in branch ka path="*"
// wahan bhejta hai). Yaani shared computer par, ya jab HR kisi employee ko dikhana chahe,
// ESS khulta hi nahi tha.
//
// ESS ka apna token (essToken) hai — staff ke login state se uska koi lena-dena nahi.
const essRoutes = (
  <>
    <Route path="/ess/login" element={<EssLogin />} />
    <Route path="/ess" element={<EssLayout />}>
      <Route index element={<EssHome />} />
      <Route path="change-password" element={<EssChangePassword />} />
      <Route path="salary" element={<EssSalary />} />
      <Route path="attendance" element={<EssAttendance />} />
      <Route path="leave" element={<EssLeave />} />
      <Route path="profile" element={<EssProfile />} />
    </Route>
  </>
);

// Platform (Tier 0 / super admin) ke routes — ESS ki tarah DONO branch me.
//
// Wahi wajah jo ESS ke comment me upar likhi hai: iska apna token (platformToken) hai,
// staff ke login state se koi lena-dena nahi. Agar sirf logged-out branch me rakhte to
// browser me staff ka session khula hone par /platform/login seedha '/' par phenk diya jaata.
const PlatformLogin = lazy(() => import('./platform/PlatformLogin.jsx'));
const PlatformLayout = lazy(() => import('./platform/PlatformLayout.jsx'));
const PlatformHospitals = lazy(() => import('./platform/PlatformHospitals.jsx'));
const PlatformAudit = lazy(() => import('./platform/PlatformAudit.jsx'));
const PlatformChangePassword = lazy(() => import('./platform/PlatformChangePassword.jsx'));
// Har screen ka manual — sirf Tier 0 badal sakta hai, kyunki content poore product
// ka hai (har hospital ko wahi dikhta hai). Wajah → HELP_MANUAL.md
const PlatformHelp = lazy(() => import('./platform/PlatformHelp.jsx'));

const platformRoutes = (
  <>
    <Route path="/platform/login" element={<PlatformLogin />} />
    <Route path="/platform" element={<PlatformLayout />}>
      <Route index element={<PlatformHospitals />} />
      <Route path="audit" element={<PlatformAudit />} />
      <Route path="help" element={<PlatformHelp />} />
      <Route path="change-password" element={<PlatformChangePassword />} />
    </Route>
  </>
);
const WardBedBoard = lazy(() => import('./pages/Ward/WardBedBoard.jsx'));
const WardMedRound = lazy(() => import('./pages/Ward/WardMedRound.jsx'));


const GSTR1Report = lazy(() => import('./pages/MEDICINE/GSTR1Report.jsx'));
const GSTR2Report = lazy(() => import('./pages/MEDICINE/GSTR2Report.jsx'));
const MedicineDuePaymentCollect = lazy(() => import('./pages/MedicineDuePaymentCollect.jsx'));
const MediVendorDueReport = lazy(() => import('./pages/MEDICINE/MediVendorDueReport.jsx'));
const AuditReport = lazy(() => import('./pages/AuditReport.jsx'));
const PurchaseVsSaleReport = lazy(() => import('./pages/PurchaseVsSaleReport.jsx'));
const AyushmanReport = lazy(() => import('./pages/AyushmanReport.jsx'));

// Lightweight fallback shown while a lazily-loaded page chunk downloads.
function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748b', fontFamily: 'system-ui, sans-serif', fontSize: 14, gap: 10 }}>
      <span style={{ width: 18, height: 18, border: '3px solid #cbd5e1', borderTopColor: '#0088cc', borderRadius: '50%', display: 'inline-block', animation: 'appspin 0.8s linear infinite' }} />
      Loading…
      <style>{`@keyframes appspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function App() {
  // User state: null means logged out, object means logged in
  const [user, setUser] = useState(null);
  // Loading state taaki check karte waqt screen blink na kare
  const [loading, setLoading] = useState(true);

  // ✅ FIX: On app load, check for JWT token in sessionStorage (Not localStorage)
  useEffect(() => {
    // Kyunki Login.js sessionStorage use kar raha hai, yahan bhi wahi check karein
    const token = sessionStorage.getItem('token');

    if (token) {
      setUser({ isAuthenticated: true });
    } else {
      setUser(null);
    }

    setLoading(false);
  }, []);

  // Function to call after successful login
  const handleLoginSuccess = (user) => {
    setUser({ isAuthenticated: true });
  };

  // ✅ FIX: Logout function clears sessionStorage
  const handleLogout = () => {
    sessionStorage.clear(); // Token, LoginId, HospitalId sab saaf
    setUser(null);
  };

  if (loading) {
    // Jab tak token check ho raha ho, loading screen dikhayein
    return <div className="d-flex justify-content-center align-items-center vh-100">Loading...</div>;
  }

  // Agar logged out hai to login page dikhayein
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        {essRoutes}
        {platformRoutes}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // User logged in hai, toh layout aur inner routes dikhayein
  return (
    <ThemeProvider>

      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ESS staff ke login state se azaad hai — isliye Layout ke bahar, aur logged-in
            branch me bhi. Warna niche ka path="*" ise '/' par phenk deta hai. */}
        {essRoutes}
        {platformRoutes}
        <Route path="/" element={<Layout onLogout={handleLogout} />}>
          <Route index element={<Dashboard />} />
          <Route path="/profile" element={<HospitalProfile />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/ChargeMaster" element={<ChargeMaster />} />
          <Route path="/DepartmentForm" element={<DepartmentForm />} />
          <Route path="/EditDepartmentList" element={<EditDepartmentList />} />
          <Route path="/EmployeeForm" element={<EmployeeForm />} />
          <Route path="/EditEmployeeList" element={<EditEmployeeList />} />
          <Route path="/opd/list" element={<OPDList />} />
          <Route path="/opd/register" element={<PatientRegister />} />
          <Route path="/Room/Form" element={<RoomForm />} />
          <Route path="/Room/Booking/Form" element={<IPDRoomBookingForm />} />
          <Route path="/user/create" element={<UserCreation />} />
          <Route path="/user/Role/Creation" element={<UserRolesManager />} />
          <Route path="/UserRolesTable" element={<UserRolesTable />} />
          <Route path="/UsersTable" element={<UsersTable />} />
          <Route path="/IpdList" element={<IpdList />} />
          <Route path="/EditRoom" element={<EditRoomForm />} />
          <Route path="/IpdAdvance" element={<IpdAdvanceForm />} />
          <Route path="/IpdServiceDetails" element={<IPDServiceDetailForm />} />
          <Route path="/IpdServiceDetailsTable" element={<IPDServiceDetailsTable />} />
          <Route path="/IpdRoomUpdate" element={<IPDRoomUpdate />} />
          <Route path="/IPDAdvanceTable" element={<IPDAdvanceTable />} />
          <Route path="/DischargeBill" element={<DischargeBill />} />
          <Route path="/DischargeList" element={<DischargeList />} />
          <Route path="/BillingForm" element={<BillingForm />} />
          <Route path="/BillList" element={<BillList/>} />
          <Route path="/Billpopup" element={<Billpopup/>} />
          <Route path="/BillingPayment" element={<BillingPayment/>} />
          <Route path="/ReportBillSummary" element={<ReportBillSummary/>} />
          <Route path="/ReportBillPayment" element={<ReportBillPayment/>} />
          <Route path="/ReportDue" element={<ReportDue/>} />
          <Route path="/Report" element={<Report/>} />
          <Route path="/ExpenseForm" element={<ExPDTForm/>} />
          <Route path="/AccountMasterForm" element={<AccountMasterForm/>} />
          <Route path="/BankEntryForm" element={<BankDTForm/>} />
          <Route path="/ExPDTReport" element={<ExPDTSearchForm/>} />
          <Route path="/DaybookReport" element={<DaybookReport/>} />
          <Route path="/DiscountAuthorityForm" element={<DiscountAuthorityForm/>} />
          <Route path="/PharmaCompanyForm" element={<PharmaCompanyForm/>} />
          <Route path="/MedCategoryForm" element={<MedCategoryDTForm/>} />
          <Route path="/MedBrandForm" element={<MedBrandForm/>} />
          <Route path="/MedicineForm" element={<MedicineForm/>} />
          <Route path="/MedicineSearchList" element={<MedicineSearchList/>} />
          <Route path="/VendorForm" element={<VendorDTForm/>} />
          <Route path="/MedPurchase" element={<MedPurchase/>} />
          <Route path="/MedicinePurchaseSearch" element={<MedicinePurchaseSearch/>} />
          <Route path="/MedicineSaleForm" element={<MedicineSaleForm/>} />
          <Route path="/MedicineSaleSearch" element={<MedicineSaleSearch/>} />
          <Route path="/MedicineSaleEdit" element={<MedicineSaleEditModal/>} />
          <Route path="/StockOutForm" element={<StockOutForm/>} />
          <Route path="/StockOutSearch" element={<StockOutSearch/>} />
          <Route path="/DebitVoucher" element={<DebitVoucher/>} />
          <Route path="/DebitVoucherSearch" element={<DebitVoucherSearch/>} />
          <Route path="/DebitVoucherEdit" element={<DebitVoucherEditModal/>} />
          <Route path="/PharmacyDashboard" element={<PharmacyDashboard/>} />
          <Route path="/InventoryReports" element={<InventoryReports/>} />
           <Route path="/SalesReports" element={<SalesReports/>} />
           <Route path="/MedProfitReport" element={<MedProfitReport/>} />
           <Route path="/MedDeadStockReport" element={<MedDeadStockReport/>} />
           <Route path="/MedCustomerDueReport" element={<MedCustomerDueReport/>} />
           <Route path="/MedDiscountReport" element={<MedDiscountReport/>} />
           <Route path="/MedVendorStatement" element={<MedVendorStatement/>} />
           <Route path="/MedBatchStockReport" element={<MedBatchStockReport/>} />
           <Route path="/MedH1Register" element={<MedH1Register/>} />
           <Route path="/MedConsumptionReport" element={<MedConsumptionReport/>} />
            <Route path="/PurchaseReports" element={<PurchaseReports/>} />

             <Route path="/ExpiryReports" element={<ExpiryReports/>} />
              <Route path="/FinancialReports" element={<FinancialReports/>} />
              <Route path="/DashboardReport" element={<DashboardReport/>} />  
               <Route path="/PrescriptionCreate" element={<PrescriptionCreate/>} /> 

               <Route path="/ReceiptSettings" element={<ReceiptSettings/>} /> 
                <Route path="/PackagesMaster" element={<PackageMaster/>} /> 
                

               <Route path="/PrescriptionHistory" element={<PrescriptionHistory/>} />
              <Route path="/IpdReport" element={<IpdReport />} />
               <Route path="/UsgMasterPage" element={<UsgMasterPage />} />
                <Route path="/UsgReportPage" element={<UsgReportPage />} />
                <Route path="/UsgReportHistory" element={<UsgReportHistory />} />
                <Route path="/UsgReportHeaderConfig" element={<USGReportHeaderConfig />} />
                <Route path="/VendorPaymentForm" element={<VendorPaymentForm />} />
                 <Route path="/FinancialLedger" element={<FinancialLedger />} />

                  <Route path="GSTR1Report" element={<GSTR1Report />} />

              <Route path="/AIChatPage" element={<AIChatPage/>} />
                  
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>




        {/* =========================================
            2. DEDICATED PHARMACY ERP ROUTES 
            (No Main Sidebar, Uses Slim Top Navbar)
            ========================================= */}
        <Route path="/pharmacy" element={<ModuleGuard module="Pharmacy" label="Pharmacy"><PharmacyLayout /></ModuleGuard>}>
          
          {/* Default Route for /pharmacy */}
          <Route index element={<Navigate to="dashboard-report" replace />} />
          <Route path="dashboard" element={<PharmacyDashboard />} />

          {/* Master Menus */}
          <Route path="category" element={<MedCategoryDTForm />} />
          <Route path="brand" element={<MedBrandForm />} />
          <Route path="medicine" element={<MedicineForm />} />
          <Route path="medicine-search" element={<MedicineSearchList />} />
          <Route path="h1-tagging" element={<MedH1Tagging />} />
          <Route path="pharma-company" element={<PharmaCompanyForm />} />
          <Route path="vendor" element={<VendorDTForm />} />

          {/* Purchase Menus */}
          <Route path="purchase" element={<MedPurchase />} />
          <Route path="purchase-search" element={<MedicinePurchaseSearch />} />
          <Route path="vendor-payment" element={<VendorPaymentForm />} />

          {/* Sales & POS Menus */}
          {/* key forces a remount when switching Sale<->Return so the mode resets */}
          <Route path="sale" element={<MedicineSaleForm key="sale" initialMode="SALE" />} />
          <Route path="sale-search" element={<MedicineSaleSearch />} />
          <Route path="sale-edit" element={<MedicineSaleEditModal />} />
          {/* Sale Return: same form, opens directly in RETURN mode */}
          <Route path="return" element={<MedicineSaleForm key="return" initialMode="RETURN" />} />

          {/* Inventory Menus */}
          <Route path="stock-out" element={<StockOutForm />} />
          <Route path="stock-out-search" element={<StockOutSearch />} />
          <Route path="debit-voucher" element={<DebitVoucher />} />
          <Route path="debit-voucher-search" element={<DebitVoucherSearch />} />
          <Route path="debit-voucher-edit" element={<DebitVoucherEditModal />} />

          {/* Reports Menus */}
          <Route path="dashboard-report" element={<DashboardReport />} />
          <Route path="inventory-reports" element={<InventoryReports />} />
          <Route path="sales-reports" element={<SalesReports />} />
          <Route path="purchase-reports" element={<PurchaseReports />} />
          <Route path="expiry-reports" element={<ExpiryReports />} />
          <Route path="financial-reports" element={<FinancialReports />} />
          <Route path="financial-ledger" element={<FinancialLedger />} />
          <Route path="GSTR1Report" element={<GSTR1Report />} />
          <Route path="GSTR2Report" element={<GSTR2Report />} />
           <Route path="MedicineDuePaymentCollect" element={<MedicineDuePaymentCollect />} />
            <Route path="MediVendorDueReport" element={<MediVendorDueReport />} />

          {/* Purchase vs Sale (date-wise) */}
          <Route path="purchase-vs-sale" element={<PurchaseVsSaleReport />} />

          {/* Ayushman report — scheme bills + inline due collection */}
          <Route path="ayushman-report" element={<AyushmanReport />} />

          {/* Profit / Margin */}
          <Route path="profit-report" element={<MedProfitReport />} />

          {/* Dead / Non-moving Stock */}
          <Route path="dead-stock" element={<MedDeadStockReport />} />

          {/* Customer Outstanding (receivables) */}
          <Route path="customer-due" element={<MedCustomerDueReport />} />

          {/* Discount Analysis */}
          <Route path="discount-report" element={<MedDiscountReport />} />

          {/* Vendor Statement / Ledger */}
          <Route path="vendor-statement" element={<MedVendorStatement />} />

          {/* Batch-wise Stock */}
          <Route path="batch-stock" element={<MedBatchStockReport />} />

          {/* Schedule H1 Register */}
          <Route path="h1-register" element={<MedH1Register />} />

          {/* Hospital Consumption (internal use) */}
          <Route path="consumption-report" element={<MedConsumptionReport />} />

          {/* Admin Audit Reports (edit/delete tracking) */}
          <Route path="sale-audit" element={<AuditReport type="SALE" />} />
          <Route path="purchase-audit" element={<AuditReport type="PURCHASE" />} />

          
        </Route>




{/* =========================================
            3. DEDICATED ADMIN REPORTS CENTER
            ========================================= */}
        <Route path="/admin-reports" element={<AdminReportLayout />}>
          {/* Default redirect */}
          <Route index element={<Navigate to="billing-summary" replace />} />

          {/* Clinical & Wards */}
          <Route path="opd-list" element={<OPDList />} />
          <Route path="ipd-list" element={<IpdList />} />
          <Route path="ipd-report" element={<IpdReport />} />

          {/* Billing & Revenue */}
          <Route path="billing-summary" element={<ReportBillSummary />} />
          <Route path="billing-payment" element={<ReportBillPayment />} />
          <Route path="due-list" element={<ReportDue />} />

          {/* Accounts & Finance */}
          <Route path="expense-report" element={<ExPDTSearchForm />} />
          <Route path="daybook" element={<DaybookReport />} />
          <Route path="financial-ledger" element={<FinancialLedger />} />

          {/* Pharmacy Analytics */}
          <Route path="pharma-dashboard" element={<DashboardReport />} />
          <Route path="pharma-inventory" element={<InventoryReports />} />
          <Route path="pharma-purchase" element={<PurchaseReports />} />
          <Route path="pharma-sales" element={<SalesReports />} />
          <Route path="pharma-profit" element={<MedProfitReport />} />
          <Route path="pharma-dead-stock" element={<MedDeadStockReport />} />
          <Route path="pharma-customer-due" element={<MedCustomerDueReport />} />
          <Route path="pharma-discount" element={<MedDiscountReport />} />
          <Route path="pharma-vendor-statement" element={<MedVendorStatement />} />
          <Route path="pharma-batch-stock" element={<MedBatchStockReport />} />
          <Route path="pharma-h1-register" element={<MedH1Register />} />
          <Route path="pharma-consumption" element={<MedConsumptionReport />} />
          <Route path="pharma-expiry" element={<ExpiryReports />} />
          <Route path="pharma-financial" element={<FinancialReports />} />

          {/* Pharmacy Audit & Movement (same pages also under /pharmacy) */}
          <Route path="purchase-vs-sale" element={<PurchaseVsSaleReport />} />
          <Route path="sale-audit" element={<AuditReport type="SALE" />} />
          <Route path="purchase-audit" element={<AuditReport type="PURCHASE" />} />
        </Route>




        {/* =========================================
            4. DEDICATED CLINICAL / RECEPTION DESK MODE
            ========================================= */}
        <Route path="/clinical" element={<ClinicalLayout />}>
          <Route index element={<Navigate to="register" replace />} />
          
          {/* In paths ke aage aapne jo components already banaye hain (jaise PatientRegister, OPDList), unhi ko link karenge */}
          <Route path="register" element={<PatientRegister />} />
          <Route path="opd-list" element={<OPDList />} />
          <Route path="ipd-list" element={<IpdList />} />
     {/* Room Booking & List */}
          <Route path="room-booking" element={<IPDRoomBookingForm />} />
          <Route path="room-update" element={<IPDRoomUpdate />} /> {/* ✅ List Route Added */}
          
          {/* Service Details & List */}
          <Route path="service-details" element={<IPDServiceDetailForm />} />
          <Route path="service-table" element={<IPDServiceDetailsTable />} /> {/* ✅ List Route Added */}
          
          {/* Advance & List */}
          <Route path="advance" element={<IpdAdvanceForm />} />
          <Route path="advance-table" element={<IPDAdvanceTable />} />
          <Route path="discharge-bill" element={<DischargeBill />} />
          <Route path="discharge-list" element={<DischargeList />} />
          <Route path="estimate-list" element={<EstimateBillList />} />
          <Route path="documents" element={<PatientDocuments />} />
          <Route path="advance-registrations" element={<AdvanceRegistrationList />} />

          {/* GST Billing (IPD services) */}
          <Route path="gst-billing" element={<GstIpdBilling />} />
          <Route path="gst-service-master" element={<GstServiceMaster />} />
          <Route path="gst-billing-list" element={<GstBillList />} />
          <Route path="gst-report" element={<GstReport />} />
        </Route>

        {/* =========================================
            IPD WARD MONITORING MODE (nursing station)
            ========================================= */}
        <Route path="/ward" element={<ModuleGuard module="IPD WARD" label="IPD Ward"><WardLayout /></ModuleGuard>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<WardDashboard />} />
          <Route path="beds" element={<WardBedBoard />} />
          {/* Poore ward ki dawa ek soochi me, samay ke kram me. Ye JAAN-BOOJHKAR
              chart ke bahar hai -- iska aakar "samay" hai, "mareez" nahi.
              Wajah pages/Ward/WardMedRound.jsx ke ooper likhi hai. */}
          <Route path="med-round" element={<WardMedRound />} />
          <Route path="patient/:ipdId" element={<WardPatientChart />} />
          {/* Chart ka har hissa apne raaste par -- URL share ho sake, back button
              chale, aur F1 ka manual har hisse ka alag ho. Wajah WardSubNav me. */}
          <Route path="patient/:ipdId/:tab" element={<WardPatientChart />} />
        </Route>

        {/* =========================================
            HRMS MODE (Human Resource Management)
            ========================================= */}
        <Route path="/hrms" element={<ModuleGuard module="HRMS" label="HRMS"><HrmsLayout /></ModuleGuard>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<HrmsDashboard />} />
          <Route path="dashboard/cxo" element={<HrmsCxoDashboard />} />
          <Route path="employees" element={<HrmsEmployees />} />
          <Route path="employee/:id" element={<HrmsEmployeeProfile />} />
          <Route path="shifts" element={<HrmsShifts />} />
          <Route path="holidays" element={<HrmsHolidays />} />
          <Route path="attendance" element={<HrmsAttendance />} />
          <Route path="attendance/monthly" element={<HrmsAttendanceMonthly />} />
          <Route path="attendance/biometric" element={<HrmsBiometricImport />} />
          <Route path="leave" element={<HrmsLeave />} />
          <Route path="leave/types" element={<HrmsLeaveTypes />} />
          <Route path="leave/balance" element={<HrmsLeaveBalance />} />
          <Route path="leave/encash" element={<HrmsLeaveEncash />} />
          <Route path="payroll/components" element={<HrmsSalaryComponents />} />
          <Route path="payroll/templates" element={<HrmsSalaryTemplates />} />
          <Route path="payroll/allot" element={<HrmsSalaryAllot />} />
          <Route path="advances" element={<HrmsAdvances />} />
          <Route path="loans" element={<HrmsLoan />} />
          <Route path="bonus" element={<HrmsBonus />} />
          <Route path="verify" element={<HrmsVerify />} />
          <Route path="vouchers" element={<HrmsVouchers />} />
          <Route path="payroll/run" element={<HrmsPayrollRun />} />
          <Route path="payroll/slips" element={<HrmsPayrollSlips />} />
          <Route path="tax/plan" element={<HrmsTaxPlan />} />
          <Route path="tax/declaration" element={<HrmsTaxDeclaration />} />
          <Route path="tax/form16" element={<HrmsForm16 />} />
          <Route path="tax/config" element={<HrmsTaxConfig />} />
          <Route path="challan" element={<HrmsChallan />} />
          <Route path="appraisal" element={<HrmsAppraisal />} />
          <Route path="assets" element={<HrmsAssets />} />
          <Route path="manual" element={<HrmsManual />} />
          <Route path="candidates" element={<HrmsCandidates />} />
          <Route path="appointments" element={<HrmsAppointments />} />
          <Route path="trainings" element={<HrmsTrainings />} />
          <Route path="reports" element={<HrmsReports />} />
          <Route path="doc-expiry" element={<HrmsDocExpiry />} />
          <Route path="roster" element={<HrmsRoster />} />
          <Route path="attendance/regularize" element={<HrmsAttRegularize />} />
          <Route path="overtime" element={<HrmsOvertime />} />
          <Route path="exit" element={<HrmsExit />} />
          <Route path="exit/:id" element={<HrmsExitDetail />} />
          <Route path="audit" element={<HrmsAudit />} />
        </Route>

        {/* =========================================
            INVENTORY MODE (MID 9) — General Store & Assets
            I0: shell + dashboard. Baaki sections phase-wise (placeholder abhi).
            ========================================= */}
        <Route path="/inventory" element={<InventoryLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<InventoryDashboard />} />
          {/* I1 Masters */}
          <Route path="category" element={<InvCategory />} />
          <Route path="unit" element={<InvUnit />} />
          <Route path="store" element={<InvStore />} />
          <Route path="vendor" element={<InvVendor />} />
          <Route path="product" element={<InvProduct />} />
          <Route path="product-search" element={<InvProduct />} />
          {/* I2 Opening + Stock views */}
          <Route path="opening-stock" element={<InvOpeningStock />} />
          <Route path="stock" element={<InvStock />} />
          <Route path="low-stock" element={<InvLowStock />} />
          <Route path="ledger" element={<InvLedger />} />
          {/* I3 Purchase */}
          <Route path="purchase-order" element={<InvPurchaseOrder />} />
          <Route path="purchase" element={<InvPurchase />} />
          <Route path="purchase-search" element={<InvPurchaseSearch />} />
          {/* I4 Issue & Receive */}
          <Route path="issue" element={<InvIssue />} />
          <Route path="issue-search" element={<InvIssueSearch />} />
          {/* I5 Consumption */}
          <Route path="consumption" element={<InvConsumption />} />
          <Route path="consumption-search" element={<InvConsumptionSearch />} />
          {/* I6 Stock-Out + Verify (maker-checker) */}
          <Route path="stock-out" element={<InvStockOut />} />
          <Route path="dept-stock-out" element={<InvDeptStockOut />} />
          <Route path="verify" element={<InvVerify />} />
          {/* I7 Vendor money */}
          <Route path="vendor-payment" element={<InvVendorPayment />} />
          <Route path="debit-voucher" element={<InvDebitVoucher />} />
          <Route path="vendor-ledger" element={<InvVendorLedger />} />
          <Route path="vendor-outstanding" element={<InvVendorOutstanding />} />
          {/* I8 Assets + Reports (FINAL) */}
          <Route path="assets" element={<InvAssets />} />
          <Route path="complaints" element={<InvAssetComplaints />} />
          <Route path="maintenance" element={<InvAssets />} />
          <Route path="usage-report" element={<InvReports />} />
          <Route path="consumption-report" element={<InvConsumptionSearch />} />
          <Route path="reports" element={<InvReports />} />
          {/* Fallback — sab sections ban gaye; koi unknown path friendly stub */}
          <Route path="*" element={<InventoryPlaceholder />} />
        </Route>



        {/* =========================================
            5. DEDICATED BILLING DESK MODE
            ========================================= */}
        <Route path="/billing" element={<BillingLayout />}>
          <Route index element={<Navigate to="form" replace />} />
          
          <Route path="form" element={<BillingForm />} />
          <Route path="list" element={<BillList />} />
          <Route path="popup" element={<Billpopup />} />
          <Route path="payment" element={<BillingPayment />} />
          <Route path="due-list" element={<ReportDue />} />
          <Route path="summary" element={<ReportBillSummary />} />
          <Route path="payment-report" element={<ReportBillPayment />} />
          <Route path="report" element={<Report />} />
        </Route>





       {/* =========================================
            6. DEDICATED CENTRAL MASTER MODE
            ========================================= */}
        <Route path="/master" element={<ModuleGuard module="HMS" label="Master"><MasterLayout /></ModuleGuard>}>
          <Route index element={<Navigate to="user/create" replace />} />
          
          {/* Admin Forms & Lists */}
          <Route path="user/create" element={<UserCreation />} />
          <Route path="users-list" element={<UsersTable />} />  {/* ✅ List added here */}
          
          <Route path="user/roles" element={<UserRolesManager />} />
          {/* T12.1 — Tier 2 role model. Purani "Roles Creation" screen module-wise
              Y/N bharti hai; ye naya role model hai (role banao, usme 6 verb chuno,
              phir user ko role do). Dono saath chal rahe hain — jis user ko role
              milta hai uske liye purani table band ho jaati hai. */}
          <Route path="role-model" element={<RoleModelManager />} />
          <Route path="roles-list" element={<UserRolesTable />} /> {/* ✅ List added here */}
          
          {/* Infra Forms & Lists */}
          <Route path="department" element={<DepartmentForm />} />
          <Route path="department-list" element={<EditDepartmentList />} /> {/* ✅ List added here */}
          
          <Route path="employee" element={<EmployeeForm />} />
          <Route path="employee-list" element={<EditEmployeeList />} /> {/* ✅ List added here */}
          
          <Route path="room" element={<RoomForm />} />
          <Route path="room-list" element={<EditRoomForm />} /> {/* ✅ List added here */}
          
          {/* Accounts & Finance */}
          <Route path="account-master" element={<AccountMasterForm />} />
          <Route path="bank-entry" element={<BankDTForm />} />
          <Route path="charge-master" element={<ChargeMaster />} />
          <Route path="discount-authority" element={<DiscountAuthorityForm />} />
          <Route path="send-by" element={<SendByMasterForm />} />
          <Route path="packages" element={<PackageMaster />} />
          
          {/* Pharmacy Forms & Lists */}
          <Route path="medicine-category" element={<MedCategoryDTForm />} />
          <Route path="medicine-brand" element={<MedBrandForm />} />
          <Route path="medicine-form" element={<MedicineForm />} />
          <Route path="medicine-list" element={<MedicineSearchList />} /> {/* ✅ List added here */}
          <Route path="pharma-company" element={<PharmaCompanyForm />} />
          <Route path="vendor" element={<VendorDTForm />} />
          
          {/* Clinical/Lab */}
          <Route path="usg-master" element={<UsgMasterPage />} />
          <Route path="usg-header" element={<USGReportHeaderConfig />} />
        </Route>



      </Routes>
      </Suspense>
    </ThemeProvider>
  );
}

export default App;